import { z } from "zod";

export const SmtpConfigSchema = z.object({
  host: z.string().trim().min(1, "Hostname is required").max(253),
  port: z.union([z.literal(465), z.literal(587)]),
  secure: z.boolean().default(false), // true for 465, false for other ports
  user: z.string().min(1, "Username is required"),
  pass: z.string().min(1, "Password is required"), // We will encrypt this on save, but schema validates the input
  fromEmail: z.string().email("Invalid email address"),
  fromName: z.string().optional(),
}).superRefine((value, ctx) => {
  if (value.port === 465 && !value.secure) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["secure"],
      message: "Port 465 requires implicit TLS",
    });
  }
  if (value.port === 587 && value.secure) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["secure"],
      message: "Port 587 uses STARTTLS",
    });
  }
});

export const MicrosoftGraphConfigSchema = z.object({
  authMode: z.enum(["DELEGATED", "APPLICATION"]).default("DELEGATED"),
  tenantId: z.string().trim().min(1, "Tenant ID is required"), // Azure Directory Tenant ID
  clientId: z.string().trim().min(1).optional(), // Legacy delegated configurations only
  clientSecret: z.string().trim().optional(),
  refreshToken: z.string().min(1).optional(), // Encrypted for delegated mode
  userEmail: z.string().email("Invalid email address"),
  servicePrincipalObjectId: z.string().trim().min(1).optional(),
  rbacScopeVerifiedAt: z.string().datetime().optional(), // Legacy assertion; not independent verification.
  rbacScopeAssertion: z.object({ kind: z.literal("ADMIN_ASSERTION"), assertedAt: z.string().datetime(), actorId: z.string().uuid() }).strict().optional(),
  expiresAt: z.number().int().optional(),
}).superRefine((value, ctx) => {
  if (value.authMode === "DELEGATED" && !value.refreshToken) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["refreshToken"],
      message: "Refresh Token is required for delegated Microsoft 365",
    });
  }
  if (value.authMode === "APPLICATION" && (value.refreshToken || value.clientSecret)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [value.refreshToken ? "refreshToken" : "clientSecret"],
      message: "Application mode uses the platform credential and must not store tenant secrets",
    });
  }
});

export type SmtpConfig = z.infer<typeof SmtpConfigSchema>;
export type MicrosoftGraphConfig = z.infer<typeof MicrosoftGraphConfigSchema>;
